import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useTheme } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchShipments, changePage, clear } from '#action/shipment/shipments-paginated';
import { fetchMeta } from '#action/meta/device';
import PageBar from '#component/general/page-bar';
import NotFoundMessage from '#component/general/not-found-message';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import filterConfig from './filter/config';
import { getSearchParams } from './helper';
import { useShipmentsLocalStorageSorting } from './hooks';
import CardsView from './cards-view';
import TableView from './table-view/table-view-new';
import {
  Cnt,
  AddButtonLabel,
  AddBtn,
  StyledPagination,
  PaginationPlaceholder,
  MobilePaginationPlaceholder
} from './styles';
import { useError, useUrlPagination } from '#hooks';
import { setDocumentTitle } from '#helper';

const Shipment = props => {
  const { history } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.XGA));
  const account = useSelector(s => s.account, shallowEqual);
  const meta = useSelector(s => s.meta, shallowEqual);
  const {
    shipments: shipmentsPaginated,
    status,
    page,
    pages,
    error
  } = useSelector(s => s.shipmentsPaginated, shallowEqual);
  const [loading, setLoading] = useState(true);
  const {
    status: filterStatus,
    shipment: shipmentFilter
  } = useSelector(s => s.listFilter, shallowEqual);
  const filterData = useMemo(() => filterConfig, []);
  const loaded = ['succeeded', 'failed'].includes(status);
  const isShipmentsLoading = (loading || !filterStatus) && !loaded;

  const replaceHistoryParams = useCallback(
    newParams =>
      new Promise(res => {
        const { location: { search } } = history;
        const params = getSearchParams(search, newParams);

        history.replace(`?${params}`);
        res(true);
      }),
    [history]
  );

  useError(error);
  useUrlPagination({ page, pages, changePage });
  useShipmentsLocalStorageSorting();

  useEffect(() => {
    setDocumentTitle('Shipment overview');
    dispatch(updateBreadcrumb([{ label: 'Shipments' }]));
  }, [dispatch]);

  const fetchData = useCallback(async () => {
    await dispatch(fetchShipments());
    setLoading(false);
  }, [dispatch]);

  const clearData = useCallback(async () => {
    dispatch(clear());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);

  useEffect(() => {
    if (!Object.values(meta ?? {}).some(v => v.length)) {
      dispatch(fetchMeta());
    }
  }, [meta, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handlePageChange = async (event, newPage) => {
    if (page !== newPage) {
      await replaceHistoryParams({ filterType: 'page', value: newPage });
      dispatch(changePage(newPage));
    }
  };

  if (error) {
    return null;
  }

  if (!isShipmentsLoading && !shipmentsPaginated.length && !shipmentFilter) {
    return (
      <NotFoundMessage>
        <Button
          variant="contained"
          color="secondary"
          aria-label="Create shipment"
          component={Link}
          to="/addshipment"
        >
          CREATE SHIPMENT
        </Button>
      </NotFoundMessage>
    );
  }

  return (
    <section>
      <PageBar view="shipment">
        <FilterWrapper data={filterData} />
        {(account.userType <= 20 && !isMobile) &&
          <AddBtn
            size="small"
            color="secondary"
            aria-label="Add"
            component={Link}
            to="/addshipment"
          >
            <AddIcon />
            <AddButtonLabel>Create shipment</AddButtonLabel>
          </AddBtn>}
      </PageBar>
      <Cnt className="default-table">
        {isMobile ? (
          <CardsView />
        ) : (
          <TableView replaceHistoryParams={replaceHistoryParams} />
        )}
      </Cnt>
      {(!!pages && Number(pages) > 1) && !isShipmentsLoading ? (
        <StyledPagination
          shape="rounded"
          color="secondary"
          count={pages}
          page={Number(page)}
          onChange={handlePageChange}
          style={isMobile ? { paddingBottom: '90px' } : {}}
        />
      ) : (
        !isMobile ? <PaginationPlaceholder /> : <MobilePaginationPlaceholder />
      )}
    </section>
  );
};

export default Shipment;
